import React, { useEffect, useState, useCallback } from "react";
import { Button, Col, Row, Alert, Spinner } from "reactstrap";
import PropTypes from "prop-types";
import XplorPaySDK from "./XplorPaySDK";
import XPLORPAY_CONFIG from "./XplorPayConfig";
import { processXplorPayAchPayment } from "api/PaymentsAPI";
import { failAlert, successAlertCustom } from "utils/alertUtils";
import { useFromUser } from "stores/LocalStorageHelper";
import useSetAccount from "account/useSetAccount";
import useSetDeal from "features/deal/hooks/useSetDeal";

/**
 * XplorPay ACH Payment Component
 *
 * Renders XplorPay's hosted ACH payment iframe.
 * Bank account data is collected by XplorPay's iframe - never touches our code.
 */
const XplorPayAchPayment = ({
  pmtAmount,
  accountID = null,
  dealID = null,
  pmtObj,
  setPmtObj,
  addPayment,
  onSuccess,
  onError,
  toggle,
}) => {
  const dealerID = useFromUser("dealerID");
  const locationID = useFromUser("locationID");

  const [loading, setLoading] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [error, setError] = useState(null);

  const setAccount = useSetAccount();
  const setDeal = useSetDeal();

  // Initialize SDK for ACH on mount
  useEffect(() => {
    let mounted = true;

    const initializeSDK = async () => {
      try {
        setError(null);
        await XplorPaySDK.initForAch();
        if (mounted) {
          setSdkReady(true);
        }
      } catch (err) {
        console.error("Failed to initialize XplorPay SDK for ACH:", err);
        if (mounted) {
          setError(err.message || "Failed to load payment form");
        }
      }
    };

    initializeSDK();

    return () => {
      mounted = false;
      XplorPaySDK.cleanup();
    };
  }, []);

  // Handle ACH payment processing
  const handleProcessPayment = useCallback(async () => {
    if (!pmtAmount || pmtAmount <= 0) {
      setError("Please enter a valid payment amount");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get JWT token from XplorPay SDK
      const tokenResponse = await XplorPaySDK.getPaymentToken();
      const { jwt, lastFour } = tokenResponse;

      if (!jwt) {
        throw new Error("Failed to obtain payment token");
      }

      // Send JWT to our backend for ACH processing
      const paymentData = {
        jwt,
        amount: pmtAmount,
        accountId: accountID,
        dealId: dealID,
        dealerId: dealerID,
        locationId: locationID,
        lastFour,
        paymentMethod: "ACH",
      };

      const response = await processXplorPayAchPayment(paymentData);

      if (response.data && response.data.content && response.data.content.success) {
        const paymentResult = response.data.content;

        const updatedPmtObj = {
          ...pmtObj,
          processor: "xplorpay",
          transactionId: paymentResult.transactionId,
          last4: lastFour,
          status: "completed",
          method: "ACH",
        };

        if (setPmtObj) {
          setPmtObj(updatedPmtObj);
        }

        // Handle account or deal update from response
        if (accountID !== null && response.data.content.account) {
          setAccount(response.data.content.account);
        } else if (dealID !== null && response.data.content.deal) {
          setDeal(response.data.content.deal);
        }

        if (addPayment) {
          addPayment("completed");
        }

        if (onSuccess) {
          onSuccess(paymentResult);
        }

        successAlertCustom("ACH payment processed successfully!");

        if (toggle) {
          toggle();
        }
      } else {
        throw new Error(
          response.data?.content?.displayMessage ||
          response.data?.content?.errorMessage ||
          response.data?.message ||
          "ACH payment failed"
        );
      }
    } catch (err) {
      console.error("ACH payment error:", err);
      const errorMessage = err.response?.data?.message || err.message || "ACH payment failed. Please try again.";
      setError(errorMessage);
      failAlert(errorMessage);

      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [pmtAmount, accountID, dealID, dealerID, locationID, pmtObj, setPmtObj, addPayment, onSuccess, onError, toggle, setAccount, setDeal]);

  return (
    <div className="xplorpay-ach-payment-container">
      <Row className="mb-3">
        <Col sm="12">
          <h5>ACH Bank Payment</h5>
          <p className="text-muted mb-2">
            Amount: <strong>${pmtAmount?.toFixed(2) || "0.00"}</strong>
          </p>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col sm="12">
            <Alert color="danger" toggle={() => setError(null)}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {!sdkReady && !error && (
        <Row className="mb-3">
          <Col sm="12" className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading secure payment form...</p>
          </Col>
        </Row>
      )}

      {/* XplorPay hosted ACH form renders here */}
      <Row className="mb-3">
        <Col sm="12">
          <div
            id={XPLORPAY_CONFIG.paymentFormId}
            style={{
              minHeight: sdkReady ? "250px" : "0",
              border: sdkReady ? "1px solid #ddd" : "none",
              borderRadius: "4px",
              padding: sdkReady ? "15px" : "0",
              backgroundColor: "#f9f9f9",
            }}
          />
        </Col>
      </Row>

      {sdkReady && (
        <Row>
          <Col sm="12" className="text-center">
            <Button
              color="primary"
              size="lg"
              onClick={handleProcessPayment}
              disabled={loading || !pmtAmount || pmtAmount <= 0}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" /> Processing...
                </>
              ) : (
                `Pay $${pmtAmount?.toFixed(2) || "0.00"} via ACH`
              )}
            </Button>
          </Col>
        </Row>
      )}

      <Row className="mt-3">
        <Col sm="12" className="text-center">
          <small className="text-muted">
            <i className="fa fa-lock mr-1"></i>
            Secured by XplorPay. Bank data is encrypted and never stored on our servers.
          </small>
        </Col>
      </Row>
    </div>
  );
};

XplorPayAchPayment.propTypes = {
  pmtAmount: PropTypes.number.isRequired,
  accountID: PropTypes.number,
  dealID: PropTypes.number,
  pmtObj: PropTypes.object,
  setPmtObj: PropTypes.func,
  addPayment: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  toggle: PropTypes.func,
};

export default XplorPayAchPayment;
